'use client';

import { useState, useCallback } from 'react';
import type { Section, FormInstance, FormValues, StepConfig } from '../types';
import { findSectionById, findCardByIdGlobal, clearSectionFormData } from '../utils/sections';
import { getStepFields } from '../utils/steps';
import { initializeCardForm } from '../utils/navigationHelpers';

interface UseSectionManagementReturn {
  completedForms: Record<string, FormInstance[]>;
  setCompletedForms: React.Dispatch<React.SetStateAction<Record<string, FormInstance[]>>>;
  currentFormInstanceId: string | null;
  setCurrentFormInstanceId: React.Dispatch<React.SetStateAction<string | null>>;
  currentSectionId: string | null;
  setCurrentSectionId: React.Dispatch<React.SetStateAction<string | null>>;
  currentStepIndex: number;
  setCurrentStepIndex: React.Dispatch<React.SetStateAction<number>>;
  handleSectionClick: (sectionId: string, onNavigate: (stepId: string) => void) => void;
  handleSectionEdit: (
    sectionId: string,
    instanceId: string,
    onNavigate: (stepId: string) => void
  ) => void;
  saveCompletedInstance: (
    sectionInfo: { sectionId: string; section: Section; stepIndex: number },
    instanceId: string,
    formData: FormValues
  ) => void;
}

/**
 * Custom hook for managing section form instances
 */
export function useSectionManagement(
  allSteps: StepConfig[],
  stepsById: Record<string, StepConfig>,
  values: FormValues,
  setValues: React.Dispatch<React.SetStateAction<FormValues>>,
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string | undefined>>>,
  setTouched: React.Dispatch<React.SetStateAction<Record<string, boolean | undefined>>>,
  _setCurrentStepId: (stepId: string) => void,
  completedForms: Record<string, FormInstance[]>,
  setCompletedForms: React.Dispatch<React.SetStateAction<Record<string, FormInstance[]>>>
): UseSectionManagementReturn {
  const [currentFormInstanceId, setCurrentFormInstanceId] = useState<string | null>(null);
  const [currentSectionId, setCurrentSectionId] = useState<string | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);

  // Handler for section/card CTA clicks - start a new form instance
  const handleSectionClick = useCallback(
    (cardOrSectionId: string, onNavigate: (stepId: string) => void) => {
      // Check if it's a step ID directly (e.g., step-9-1-confirmation)
      if (stepsById[cardOrSectionId]) {
        // It's a step ID - navigate directly without creating an instance
        onNavigate(cardOrSectionId);
        return;
      }

      // First, try to find if it's a card
      const cardResult = findCardByIdGlobal(cardOrSectionId, allSteps);

      if (cardResult) {
        // Handle card-based section
        const { card, section } = cardResult;
        const firstStepId = card.stepIds[0];
        const step = stepsById[firstStepId];
        if (!step) return;

        // Initialize card form using helper function
        initializeCardForm(card, section.id, stepsById, values, {
          setCurrentFormInstanceId,
          setCurrentSectionId,
          setCurrentStepIndex,
          setValues,
          setErrors,
          setTouched,
        });

        // Navigate to first step
        onNavigate(firstStepId);
        return;
      }

      // Legacy: Handle section without cards
      const section = findSectionById(cardOrSectionId, allSteps);
      if (!section || !section.stepIds || section.stepIds.length === 0) return;

      // Start with the first stepId in the array
      const firstStepId = section.stepIds[0];
      const step = stepsById[firstStepId];
      if (!step) return;

      // Generate new instance ID
      const instanceId = `${cardOrSectionId}-${Date.now()}`;
      setCurrentFormInstanceId(instanceId);
      setCurrentSectionId(cardOrSectionId);
      setCurrentStepIndex(0);

      // Clear form values for all steps in this section
      const clearedValues = clearSectionFormData(section, stepsById, values);
      setValues(clearedValues);
      setErrors({});
      setTouched({});

      // Use onNavigate callback instead of setCurrentStepId directly
      onNavigate(firstStepId);
    },
    [allSteps, stepsById, values, setValues, setErrors, setTouched]
  );

  // Handler for section/card Edit clicks - load existing form instance
  const handleSectionEdit = useCallback(
    (cardOrSectionId: string, instanceId: string, onNavigate: (stepId: string) => void) => {
      // First, try to find if it's a card
      const cardResult = findCardByIdGlobal(cardOrSectionId, allSteps);

      if (cardResult) {
        const { card, section } = cardResult;

        // Find the completed form instance
        const completedInstances = completedForms[card.groupKey] || [];
        const instance = completedInstances.find((inst) => inst.instanceId === instanceId);
        if (!instance) {
          console.warn(`Instance ${instanceId} not found in completedForms for card ${card.id}`);
          return;
        }

        // Check if this is an addMore card by finding a step with matching groupKey
        const addMoreStep = allSteps.find((step) => {
          const config = step.addMoreConfig;
          return config && config.groupKey === card.groupKey;
        });

        if (addMoreStep && addMoreStep.addMoreConfig) {
          // SECTION CARD EDIT FOR ADDMORE: Handle addMore card edit from section list
          const config = addMoreStep.addMoreConfig;

          // Check if there are existing instances
          const existingInstances = completedForms[config.groupKey] || [];

          // If there are multiple instances or instances exist, just navigate without loading data
          // User should use inline card edit instead
          if (existingInstances.length > 0) {
            // Clear any form values to prevent auto-showing form
            const clearedValues = { ...values };
            config.fieldIds.forEach((fieldId) => {
              delete clearedValues[fieldId];
            });
            setValues(clearedValues);
            setErrors({});
            setTouched({});

            // Navigate to the addMore step - form will NOT show, inline cards will be visible
            onNavigate(addMoreStep.id);
            return;
          }

          // Load only addMore field data (not all card fields)
          const updatedValues = { ...values };
          config.fieldIds.forEach((fieldId) => {
            if (instance.data[fieldId] !== undefined) {
              updatedValues[fieldId] = instance.data[fieldId];
            }
          });
          setValues(updatedValues);
          setErrors({});
          setTouched({});

          // Navigate to the addMore step
          // Form will be shown automatically when user lands on the step (via isFormVisible logic)
          onNavigate(addMoreStep.id);
          return;
        }

        // Regular card edit (non-addMore) - existing logic
        setCurrentFormInstanceId(instanceId);
        setCurrentSectionId(section.id);
        setCurrentStepIndex(0);

        const updatedValues = { ...values };
        card.stepIds.forEach((stepId) => {
          const cardStep = stepsById[stepId];
          if (cardStep) {
            const stepFields = getStepFields(cardStep);
            stepFields.forEach((field) => {
              // Skip if field is in excludeFieldIds (e.g., children fields for card-personal-details)
              if (card.excludeFieldIds && card.excludeFieldIds.includes(field.id)) {
                return;
              }
              // Only include if includeFieldIds is not specified, or field is in includeFieldIds
              if (!card.includeFieldIds || card.includeFieldIds.includes(field.id)) {
                if (instance.data[field.id] !== undefined) {
                  updatedValues[field.id] = instance.data[field.id];
                }
              }
            });
          }
        });
        setValues(updatedValues);
        setErrors({});
        setTouched({});

        onNavigate(card.stepIds[0]);
        return;
      }

      // Legacy: Handle section without cards
      const section = findSectionById(cardOrSectionId, allSteps);
      if (!section || !section.stepIds || section.stepIds.length === 0) return;

      // Find the completed form instance
      const groupKey = section.groupKey || cardOrSectionId;
      const completedInstances = completedForms[groupKey] || [];
      const instance = completedInstances.find((inst) => inst.instanceId === instanceId);
      if (!instance) {
        console.warn(
          `Instance ${instanceId} not found in completedForms for section ${cardOrSectionId}`
        );
        return;
      }

      // Load the instance data
      setCurrentFormInstanceId(instanceId);
      setCurrentSectionId(cardOrSectionId);
      setCurrentStepIndex(0); // Always start from first step when editing

      // Merge instance data with current values (don't overwrite other fields)
      const updatedValues = { ...values };
      section.stepIds.forEach((stepId) => {
        const sectionStep = stepsById[stepId];
        if (sectionStep) {
          const stepFields = getStepFields(sectionStep);
          stepFields.forEach((field) => {
            if (instance.data[field.id] !== undefined) {
              updatedValues[field.id] = instance.data[field.id];
            }
          });
        }
      });
      setValues(updatedValues);
      setErrors({});
      setTouched({});

      // Navigate to first step of the section
      onNavigate(section.stepIds[0]);
    },
    [allSteps, stepsById, completedForms, values, setValues, setErrors, setTouched]
  );

  // Save completed form instance
  const saveCompletedInstance = useCallback(
    (
      sectionInfo: { sectionId: string; section: Section; stepIndex: number },
      instanceId: string,
      formData: FormValues
    ) => {
      const groupKey = sectionInfo.section.groupKey || sectionInfo.sectionId;

      setCompletedForms((prev) => {
        const sectionInstances = prev[groupKey] || [];
        const existingIndex = sectionInstances.findIndex((inst) => inst.instanceId === instanceId);

        const newInstance: FormInstance = {
          instanceId,
          data: formData,
        };

        if (existingIndex >= 0) {
          // Update existing instance
          const updated = [...sectionInstances];
          updated[existingIndex] = newInstance;
          return { ...prev, [groupKey]: updated };
        } else {
          // Add new instance
          return { ...prev, [groupKey]: [...sectionInstances, newInstance] };
        }
      });
    },
    [setCompletedForms]
  );

  return {
    completedForms,
    setCompletedForms,
    currentFormInstanceId,
    setCurrentFormInstanceId,
    currentSectionId,
    setCurrentSectionId,
    currentStepIndex,
    setCurrentStepIndex,
    handleSectionClick,
    handleSectionEdit,
    saveCompletedInstance,
  };
}
